/**
 * Meta Conversions API (CAPI) client-side helper.
 *
 * Sends events to both the browser Meta Pixel AND the server-side CAPI endpoint
 * with a shared event_id for deduplication. Meta deduplicates within a 48h window.
 *
 * The CAPI request goes: browser → our server → Meta Graph API.
 * This ensures Meta gets the conversion signal even when ad blockers kill the Pixel.
 *
 * ── GDPR compliance strategy (region-aware) ──
 *
 *   WITH consent (any region): full payload (IP, UA, _fbp/_fbc cookies, hashed
 *                    email) → maximum match quality for ad optimization.
 *
 *   NO consent, opt-OUT region (US, etc.): CAPI still fires with IP+UA. Meta
 *                    rejects empty-user_data events (subcode 2804050; UA-only
 *                    bounces too - verified 2026-06-10), and these regions have
 *                    no opt-in requirement, so relaying IP+UA is lawful and
 *                    recovers conversion signal the pixel can't (no consent →
 *                    no pixel, but CAPI doesn't need it).
 *
 *   NO consent, consent-required region (EU/UK/CH/CA/BR): no CAPI call -
 *                    sending IP/UA there needs explicit consent.
 *
 * Region is decided server-side from the geo header (see consent-regions.js);
 * the browser pixel (fbq) stays consent-only regardless of region.
 *
 * Requires env vars on the server:
 *   META_PIXEL_ID           - your Meta Pixel / Dataset ID
 *   META_CAPI_ACCESS_TOKEN  - from Meta Events Manager → Settings → Conversions API
 */

import { browser } from "$app/environment";

// META_PIXEL_ID + META_CAPI_ACCESS_TOKEN are set in Vercel (Preview + Production,
// 2026-06-10). While false: client-side Pixel (fbq) still fires; server-side
// CAPI fetch is skipped.
const CAPI_ENABLED = true;

function generateEventId() {
  if (browser && crypto?.randomUUID) return crypto.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

function getMetaCookies() {
  if (!browser) return {};
  const cookies = document.cookie;
  const fbp = cookies.match(/_fbp=([^;]+)/)?.[1] || null;
  const fbc = cookies.match(/_fbc=([^;]+)/)?.[1] || null;
  return { ...(fbp && { fbp }), ...(fbc && { fbc }) };
}

/**
 * Send an event to both Meta Pixel (client) and CAPI (server).
 *
 * @param {string} eventName - Standard Meta event name
 * @param {object} [customData] - Event-specific data (value, currency, content_ids, etc.)
 * @param {object} [options]
 * @param {string} [options.eventSourceUrl] - Override the current page URL
 * @param {string} [options.userEmail] - User email (hashed server-side)
 */
export function trackMetaEvent(eventName, customData = {}, options = {}) {
  if (!browser) return;

  const eventId = generateEventId();
  const hasConsent = localStorage.getItem("marketing") === "true";

  // 1. Fire browser Meta Pixel (only with consent)
  if (hasConsent && window.fbq && typeof window.fbq === "function") {
    window.fbq("track", eventName, customData, { eventID: eventId });
  }

  // 2. Fire server-side CAPI. Lawful basis to relay IP+UA to Meta:
  //   - consent-required regions (EU/UK/...): only with explicit consent;
  //   - opt-out regions (US, etc.): allowed without a prior consent click.
  // window.__REQUIRES_CONSENT is set server-side per geo (default true). The
  // server re-checks region authoritatively from the geo header - this gate
  // just avoids firing requests the server would drop.
  const regionRequiresConsent =
    (browser ? window.__REQUIRES_CONSENT : true) !== false;
  if (!CAPI_ENABLED || (!hasConsent && regionRequiresConsent)) return;

  const payload = {
    event_name: eventName,
    event_id: eventId,
    event_source_url:
      options.eventSourceUrl || (browser ? window.location.href : ""),
    custom_data: customData,
    has_consent: hasConsent,
    ...getMetaCookies(),
  };
  if (options.userEmail) payload.user_email = options.userEmail;

  fetch("/api/meta-capi", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
    keepalive: true,
  }).catch(() => {
    // Silent fail - CAPI is best-effort, never block the user
  });
}

/**
 * Track product page view via both Pixel and CAPI.
 */
export function trackViewContentCAPI(product, variant = null) {
  if (!product) return;
  const selectedVariant = variant || product.variants?.edges?.[0]?.node;

  trackMetaEvent("ViewContent", {
    content_type: "product",
    content_ids: [selectedVariant?.id || product.id],
    content_name: product.title,
    content_category: "Electronics",
    value: parseFloat(
      selectedVariant?.price?.amount ||
        product.priceRange?.minVariantPrice?.amount ||
        0,
    ),
    currency:
      selectedVariant?.price?.currencyCode ||
      product.priceRange?.minVariantPrice?.currencyCode ||
      "EUR",
  });
}

/**
 * Track add to cart via Meta Pixel + CAPI, and Reddit Pixel.
 */
export function trackAddToCartCAPI(
  product,
  variant,
  quantity = 1,
  editionName = null,
) {
  if (!variant?.id) return;

  const productName = editionName
    ? `${product?.title || "Product"} - ${editionName}`
    : product?.title || variant.product?.title || "Product";

  const value = parseFloat(variant.price?.amount || 0) * quantity;
  const currency = variant.price?.currencyCode || "EUR";

  trackMetaEvent("AddToCart", {
    content_type: "product",
    content_ids: [variant.id],
    content_name: productName,
    content_category: "Electronics",
    value,
    currency,
    num_items: quantity,
  });

  // Reddit Pixel - requires marketing consent and pixel to be loaded
  if (
    browser &&
    localStorage.getItem("marketing") === "true" &&
    window.rdt &&
    typeof window.rdt === "function"
  ) {
    window.rdt("track", "AddToCart", { value, currency, itemCount: quantity });
  }
}

/**
 * Track checkout initiation via both Pixel and CAPI.
 */
export function trackInitiateCheckoutCAPI(cartItems) {
  if (!cartItems?.length) return;

  const contentIds = cartItems.map(
    (item) => item.node?.merchandise?.id || item.variantId,
  );
  const totalValue = cartItems.reduce((total, item) => {
    const node = item.node || item;
    const price = parseFloat(
      node.merchandise?.price?.amount || node.price || 0,
    );
    const qty = node.quantity || 1;
    return total + price * qty;
  }, 0);
  const numItems = cartItems.reduce(
    (total, item) => total + (item.node?.quantity || item.quantity || 1),
    0,
  );
  const currency =
    cartItems[0]?.node?.merchandise?.price?.currencyCode || "EUR";

  trackMetaEvent("InitiateCheckout", {
    content_type: "product",
    content_ids: contentIds,
    value: totalValue,
    currency,
    num_items: numItems,
  });
}
